/**
 * The propers page model — shared by the site that renders the pages and the
 * script that prerenders them.
 *
 * WHY THIS IS IN THE PACKAGE AND NOT IN THE SITE'S `lib/`. The same split as
 * `hymnal-pages.ts`: route enumeration is a property of the DATA, display is a
 * property of the site. `generate-seo-routes.mjs` writes the routes the
 * prerenderer builds and the app is what serves them, so if the two computed
 * slugs independently the build would emit pages the router cannot match, or the
 * router would offer pages that were never prerendered. That failure is called
 * out in the generator's own header for `sections`; this exists so it cannot
 * happen for propers.
 *
 * Pure and data-injected: nothing here imports a dataset.
 */
import { dayOrder, seasonForDayId } from './canticle/lectionary/index.js';
/** The label for days the sources do not file in the temporal cycle. */
export const OUTSIDE_TEMPORAL_CYCLE = 'Feasts, saints and occasional services';
/** Sort rank for non-temporal days: after every ranked day, before nothing. */
const NON_TEMPORAL_RANK = 1000;
const PROPER_ID_RE = /^proper-(\d{1,2})$/;
/**
 * Display title for a merged day.
 *
 * The books name the Sundays after Pentecost differently and one of the names
 * is much better to land on: the American Gradual prints "Sunday Between July
 * 24 and 30", the Gradual Psalms print "Proper 12 (Closest to July 27)", and
 * what an Episcopal musician says and searches for is "Proper 12". Since the
 * first book supplies the title and that is the Gradual, the date span would
 * otherwise become the page title and the h1.
 *
 * Only the propers are special-cased. Every other day is named the same way in
 * both books, so the source title is already the right one.
 */
function displayTitle(dayId, sourceTitle) {
    const proper = PROPER_ID_RE.exec(dayId);
    return proper ? `Proper ${Number(proper[1])}` : sourceTitle;
}
/**
 * Which group a day belongs to, and where it sorts.
 *
 * THE SOURCE'S `temporal` FLAG DECIDES, NOT THE DAY'S NAME. `dayOrder` and
 * `seasonForDayId` match on substrings, and the occasional services are full of
 * substrings that lie: "BOS Service for New Year's Eve" hits the Holy Name rule
 * and sorts into Christmas, "Vigil of St. Peter and St. Paul" hits the generic
 * vigil rule and sorts into Easter. That produced twelve groups covering eight
 * seasons, with Christmas and Easter each stated twice. The books already know
 * which section a day is printed in, so the extractors record it.
 */
function groupFor(day) {
    if (!day.temporal)
        return { group: OUTSIDE_TEMPORAL_CYCLE, order: NON_TEMPORAL_RANK };
    const order = dayOrder(day.dayId);
    if (order === 99)
        return { group: OUTSIDE_TEMPORAL_CYCLE, order: NON_TEMPORAL_RANK };
    return { group: seasonForDayId(day.dayId), order };
}
const asRite = (rite) => (rite === 'roman' || rite === 'episcopal' ? rite : null);
const asYears = (years) => {
    if (!years)
        return null;
    const known = years.filter((y) => y === 'A' || y === 'B' || y === 'C');
    return known.length ? known : null;
};
/** The order the chants are sung in, which is not the order a parser finds them. */
const TYPE_ORDER = ['introit', 'gradual', 'alleluia', 'tract', 'offertory', 'communion', 'antiphon'];
const typeRank = (t) => {
    const i = TYPE_ORDER.indexOf(t);
    return i === -1 ? TYPE_ORDER.length : i;
};
/** Sort chants into the order they are sung, then by year, then by option. */
export function sortChants(chants) {
    return [...chants].sort((a, b) => typeRank(a.type) - typeRank(b.type) ||
        (a.years?.[0] ?? '').localeCompare(b.years?.[0] ?? '') ||
        (a.option ?? 0) - (b.option ?? 0) ||
        a.pdfPage - b.pdfPage);
}
/**
 * Merge one or more books into one page per liturgical day.
 *
 * ONE PAGE PER DAY, ACROSS BOOKS — that is the reason to extract more than one.
 * A musician planning Proper 12 wants the introit, the gradual psalm and the
 * alleluia verse together, and those come from different volumes. Merging on
 * `dayId` is what puts them on one page, so `dayId` is also why the two
 * extractors share `propers_common.py`.
 *
 * THE RITE MOVES ONTO THE CHANT. The American Gradual sets Lent 2 twice, once
 * Episcopal and once Roman, and the earlier one-book model gave those separate
 * routes. Across books that breaks the join, because the Gradual Psalms set one
 * Lent 2 that belongs with both. Lent 2 is one Sunday, so it gets one page and
 * the rite becomes a label on the chants it qualifies.
 *
 * ORDER IS `dayOrder`, NOT PAGE ORDER. A single book's pagination was the
 * better sequence when there was one book; with two it is meaningless, since
 * neither book's page numbers order the other's. Days outside the temporal
 * cycle rank 99 and are grouped under `OUTSIDE_TEMPORAL_CYCLE` rather than
 * being scattered back into the seasons their names imply — which is what keeps
 * the Presentation out of Epiphany and stops a season appearing twice.
 */
export function enumerateProperDays(...books) {
    const merged = new Map();
    for (const book of books) {
        const source = { book: book.source.title, bookUrl: book.source.url };
        for (const raw of book.days) {
            const rite = asRite(raw.rite);
            const chants = sortChants(raw.chants).map((c) => ({
                ...c,
                years: asYears(c.years),
                source: { ...source, rite },
            }));
            const existing = merged.get(raw.dayId);
            if (existing) {
                existing.chants.push(...chants);
                if (!existing.books.includes(source.book))
                    existing.books.push(source.book);
                continue;
            }
            const { group, order } = groupFor(raw);
            merged.set(raw.dayId, {
                ...raw,
                title: displayTitle(raw.dayId, raw.title),
                rite,
                chants,
                slug: raw.dayId,
                season: seasonForDayId(raw.dayId),
                order,
                group,
                types: [],
                yearVaries: false,
                books: [source.book],
            });
        }
    }
    const days = [...merged.values()];
    for (const day of days) {
        day.chants = sortChants(day.chants);
        day.types = [...new Set(day.chants.map((c) => c.type))];
        day.yearVaries = day.chants.some((c) => c.years);
    }
    days.sort((a, b) => a.order - b.order || a.dayId.localeCompare(b.dayId));
    return days;
}
/** Every concrete route a book produces, index first. */
export function properRoutes(days, base = '/hymnal/propers') {
    return [base, ...days.map((d) => `${base}/${d.slug}`)];
}
